import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    TrendingUp, Percent, Calendar,
    Info, Calculator, Zap, ArrowUpRight, HelpCircle
} from 'lucide-react';

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell
} from 'recharts';

// Custom Tooltip for Recharts
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-3 border border-gray-100 shadow-xl rounded-xl">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 border-b border-gray-50 pb-1">
                    Year {label}
                </p>
                <div className="space-y-1.5">
                    {payload.map((entry, index) => (
                        <div key={index} className="flex justify-between items-center gap-6">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{entry.name}</span>
                            <span className="text-[11px] font-bold text-[#0F172A]">AED {entry.value.toLocaleString()}</span>
                        </div>
                    ))}
                    <div className="pt-1.5 border-t border-gray-50 flex justify-between items-center gap-6">
                        <span className="text-[10px] font-black text-[#1f6f6f] uppercase tracking-wider">Total Value</span>
                        <span className="text-[11px] font-black text-[#1f6f6f]">AED {payload.reduce((sum, entry) => sum + entry.value, 0).toLocaleString()}</span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

// Recharts Stacked Bar Chart Component
const StackedBarChart = ({ data }) => {
    // Transform data for Recharts
    const chartData = data.map(d => ({
        name: d.year,
        investment: d.investment,
        appreciation: d.propertyValue - d.investment,
        rentalIncome: d.totalValue - d.propertyValue,
    }));

    return (
        <div className="w-full h-full min-h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    barGap={0}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 700 }}
                        dy={10}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 700 }}
                        tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                        width={40}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                    <Bar
                        dataKey="investment"
                        name="Investment"
                        stackId="a"
                        fill="#1f6f6f"
                        radius={[0, 0, 4, 4]}
                    />
                    <Bar
                        dataKey="appreciation"
                        name="Appreciation"
                        stackId="a"
                        fill="#54a1a1"
                    />
                    <Bar
                        dataKey="rentalIncome"
                        name="Rental Income"
                        stackId="a"
                        fill="#9fc8c8"
                        radius={[4, 4, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

// Reusable Input + Slider Component
const CalculatorInput = ({ label, value, min, max, step, onChange, icon, suffix = '' }) => {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] flex items-center gap-2">
                    {icon}
                    {label}
                </label>
                <div className="relative group">
                    <input
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={(e) => onChange(Number(e.target.value))}
                        className="w-24 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 text-right text-[13px] font-bold text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all appearance-none"
                    />
                    {suffix && (
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] font-black text-gray-400 pointer-events-none">
                            {suffix}
                        </span>
                    )}
                </div>
            </div>

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-emerald-600"
            />

            <div className="flex justify-between text-[9px] text-gray-400 font-bold uppercase tracking-wider">
                <span>{min.toLocaleString()}{suffix}</span>
                <span>{max.toLocaleString()}{suffix}</span>
            </div>
        </div>
    );
};

// Investment Calculator Component
export default function InvestmentCalculator({ property, onShowHowItWorks }) {
    const [investmentAmount, setInvestmentAmount] = useState(10000);
    const [holdingPeriod, setHoldingPeriod] = useState(5);
    const [rentalYield, setRentalYield] = useState(parseFloat(property.financials?.projectedRentalYield) || 4.5);
    const [appreciation, setAppreciation] = useState(parseFloat(property.financials?.annualAppreciation) || 6.2);

    // Parse property financial data
    const tokenPrice = parseFloat(property.tokenPriceAED?.replace(/[, AED]/g, '')) || 165;

    // Calculate projections
    const projections = useMemo(() => {
        const data = [];
        let currentPropertyValue = investmentAmount;
        let accumulatedRentalIncome = 0;

        // Year 0: Baseline (Initial Investment)
        data.push({
            year: 0,
            propertyValue: Math.round(currentPropertyValue),
            rentalIncome: 0,
            totalValue: Math.round(currentPropertyValue)
        });

        // Year 1 to N
        for (let year = 1; year <= holdingPeriod; year++) {
            const yearlyRental = currentPropertyValue * (rentalYield / 100);
            accumulatedRentalIncome += yearlyRental;
            currentPropertyValue *= (1 + appreciation / 100);

            data.push({
                year,
                propertyValue: Math.round(currentPropertyValue),
                rentalIncome: Math.round(accumulatedRentalIncome),
                totalValue: Math.round(currentPropertyValue + accumulatedRentalIncome)
            });
        }
        return data;
    }, [investmentAmount, holdingPeriod, rentalYield, appreciation]);

    // Final values
    const finalProjection = projections[projections.length - 1];
    const capitalGains = finalProjection.propertyValue - investmentAmount;

    // Chart data
    const chartData = projections.map(p => ({
        year: p.year,
        investment: investmentAmount,
        propertyValue: p.propertyValue,
        rentalIncome: p.rentalIncome,
        totalValue: p.totalValue
    }));

    return (
        <div className="pt-6 pb-6 space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 tracking-wider">Investment calculator</h2>
                </div>
            </div>

            {/* Graph and Metrics Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-0 border-0"
            >
                <div className="flex flex-col gap-y-6 lg:gap-y-10">
                    {/* Metrics Row - Horizontal Above Graph */}
                    <div className="grid grid-cols-3 gap-3 lg:gap-12 pb-2">
                        <div className="flex flex-row items-start gap-2.5 lg:gap-4 text-left">
                            <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-[#1f6f6f] flex-shrink-0 mt-1 shadow-[0_0_10px_rgba(31,111,111,0.3)]" />
                            <div>
                                <p className="text-[7px] lg:text-[10px] text-gray-400 font-black uppercase tracking-[0.1em] lg:tracking-[0.2em] mb-0.5 lg:mb-1 leading-none">Investment</p>
                                <p className="text-[10px] lg:text-xl font-bold text-[#0F172A] leading-tight tracking-tight">AED {investmentAmount.toLocaleString()}</p>
                            </div>
                        </div>

                        <div className="flex flex-row items-start gap-2.5 lg:gap-4 text-left">
                            <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-[#9fc8c8] flex-shrink-0 mt-1 shadow-[0_0_10px_rgba(159,200,200,0.3)]" />
                            <div>
                                <p className="text-[7px] lg:text-[10px] text-gray-400 font-black uppercase tracking-[0.1em] lg:tracking-[0.2em] mb-0.5 lg:mb-1 leading-none">Rental income</p>
                                <p className="text-[10px] lg:text-xl font-bold text-[#0F172A] leading-tight tracking-tight">AED {finalProjection.rentalIncome.toLocaleString()}</p>
                            </div>
                        </div>

                        <div className="flex flex-row items-start gap-2.5 lg:gap-4 text-left">
                            <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-[#54a1a1] flex-shrink-0 mt-1 shadow-[0_0_10px_rgba(84,161,161,0.5)]" />
                            <div>
                                <p className="text-[7px] lg:text-[10px] text-gray-400 font-black uppercase tracking-[0.1em] lg:tracking-[0.2em] mb-0.5 lg:mb-1 leading-none">Appreciation</p>
                                <p className="text-[10px] lg:text-xl font-bold text-[#0F172A] leading-tight tracking-tight">AED {capitalGains.toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    {/* Chart Section */}
                    <div className="w-full flex justify-center">
                        <div className="h-[250px] sm:h-[320px] w-full max-w-3xl">
                            <StackedBarChart data={chartData} />
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Input Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12 py-10 border-t border-gray-100">
                {/* Investment Amount */}
                <div className="space-y-6">
                    <CalculatorInput
                        label="Initial Investment"
                        value={investmentAmount}
                        min={1000}
                        max={100000}
                        step={1000}
                        suffix="AED"
                        onChange={setInvestmentAmount}
                    />
                    <div className="flex gap-2 flex-wrap pt-2">
                        {[5000, 10000, 25000, 50000].map(amount => (
                            <button
                                key={amount}
                                onClick={() => setInvestmentAmount(amount)}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${investmentAmount === amount
                                    ? 'bg-emerald-600 text-white shadow-lg'
                                    : 'bg-white border border-gray-100 text-gray-400 hover:bg-gray-50'
                                    }`}
                            >
                                {amount >= 1000 ? `${amount / 1000}K` : amount}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Holding Period */}
                <div className="space-y-6">
                    <CalculatorInput
                        label="Holding Period"
                        value={holdingPeriod}
                        min={1}
                        max={10}
                        step={1}
                        suffix="YRS"
                        onChange={setHoldingPeriod}
                    />
                    <div className="flex gap-2 flex-wrap pt-2">
                        {[3, 5, 7, 10].map(years => (
                            <button
                                key={years}
                                onClick={() => setHoldingPeriod(years)}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${holdingPeriod === years
                                    ? 'bg-emerald-600 text-white shadow-lg'
                                    : 'bg-white border border-gray-100 text-gray-400 hover:bg-gray-50'
                                    }`}
                            >
                                {years} Years
                            </button>
                        ))}
                    </div>
                </div>

                {/* Rental Yield */}
                <CalculatorInput
                    label="Rental Yield (ROI)"
                    value={rentalYield}
                    min={1}
                    max={20}
                    step={0.1}
                    suffix="%"
                    onChange={setRentalYield}
                />

                {/* Appreciation */}
                <CalculatorInput
                    label="Annual Appreciation"
                    value={appreciation}
                    min={1}
                    max={25}
                    step={0.1}
                    suffix="%"
                    onChange={setAppreciation}
                />
            </div>
        </div>
    );
}
